import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from "../contexts/ThemeContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line
} from "recharts";

import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import UpgradeButton from './common/UpgradeButton';

const WORDS_TO_MASTERY = 30000000; // 30 million words
const WORDS_PER_MINUTE = 50; // National average typing speed

export default function Analytics() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Analytics data states
  const [totalWords, setTotalWords] = useState(0);
  const [totalBits, setTotalBits] = useState(0);
  const [averageWordsPerBit, setAverageWordsPerBit] = useState(0);
  const [streak, setStreak] = useState(0);
  const [weeklyActivity, setWeeklyActivity] = useState<any[]>([]);

  const [monthlyProgress, setMonthlyProgress] = useState<any[]>([]);
  const [masteryProgress, setMasteryProgress] = useState<any[]>([]);
  const [totalExercises, setTotalExercises] = useState(0);
  const [exerciseStreak, setExerciseStreak] = useState(0);
  const [activityHeatmap, setActivityHeatmap] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        console.log('Fetching analytics data...');
        
        // Fetch user's bits, exercises, and ideas in parallel
        const [bitsSnapshot, exercisesSnapshot, ideasSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'drafts'), where('userId', '==', user.uid))),
          getDocs(query(collection(db, 'exerciseSaves'), where('userId', '==', user.uid))),
          getDocs(query(collection(db, 'ideas'), where('userId', '==', user.uid))),
        ]);

        const bits = bitsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Exercise stats
        const exercises = exercisesSnapshot.docs.map(d => d.data());
        setTotalExercises(exercises.length);

        const ideas = ideasSnapshot.docs.map(d => d.data());

        // Exercise streak (consecutive days with a daily-word exercise)
        const exerciseDates = new Set(
          exercises.filter(e => e.type === 'daily-word' && e.date).map(e => e.date as string)
        );
        const todayET = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
        let exStreak = 0;
        const cursor = new Date(todayET + 'T12:00:00');
        if (!exerciseDates.has(todayET)) cursor.setDate(cursor.getDate() - 1);
        while (true) {
          const dateStr = new Intl.DateTimeFormat('en-CA').format(cursor);
          if (exerciseDates.has(dateStr)) {
            exStreak++;
            cursor.setDate(cursor.getDate() - 1);
          } else break;
        }
        setExerciseStreak(exStreak);

        // Activity heatmap: combine bit activity + exercise completions (last 12 weeks)
        const heatmap: Record<string, number> = {};
        bits.forEach((bit: any) => {
          [bit.createdAt, bit.updatedAt].forEach(ts => {
            if (!ts) return;
            const dateStr = new Intl.DateTimeFormat('en-CA').format(new Date(ts));
            heatmap[dateStr] = (heatmap[dateStr] || 0) + 1;
          });
        });
        exercises.forEach(e => {
          if (e.date) heatmap[e.date] = (heatmap[e.date] || 0) + 1;
        });
        setActivityHeatmap(heatmap);
        
        // Calculate total bits
        setTotalBits(bits.length);
        
        // Calculate total words and average words per bit
        const countWords = (text: string) =>
          text.trim().split(/\s+/).filter((word: string) => word.length > 0).length;

        let bitsWordCount = 0;
        bits.forEach((bit: any) => {
          // Only count words if bit has content
          if (bit && typeof bit.content === 'string') {
            bitsWordCount += countWords(bit.content);
          }
        });

        let exercisesWordCount = 0;
        exercises.forEach((exercise: any) => {
          if (exercise && typeof exercise.content === 'string') {
            exercisesWordCount += countWords(exercise.content);
          }
        });

        let ideasWordCount = 0;
        ideas.forEach((idea: any) => {
          if (idea && typeof idea.description === 'string') {
            ideasWordCount += countWords(idea.description);
          }
        });

        const wordCount = bitsWordCount + exercisesWordCount + ideasWordCount;

        setTotalWords(wordCount);
        setAverageWordsPerBit(bits.length > 0 ? Math.round(bitsWordCount / bits.length) : 0);

        // Calculate mastery progress
        const progressPercentage = (wordCount / WORDS_TO_MASTERY) * 100;
        const hoursInvested = (wordCount / WORDS_PER_MINUTE) / 60;
        const hoursRemaining = (WORDS_TO_MASTERY - wordCount) / WORDS_PER_MINUTE / 60;

        setMasteryProgress([{
          name: 'Progress',
          value: progressPercentage,
          words: wordCount,
          hoursInvested: hoursInvested,
          hoursRemaining: hoursRemaining
        }]);
        
        // Calculate writing streak (simplified version)
        // In a real implementation, you'd need more sophisticated logic
        const streak = calculateStreak([...bits, ...ideas]);
        setStreak(streak);
        
        // Generate weekly activity data
        const weeklyData = generateWeeklyActivity(bits);
        setWeeklyActivity(weeklyData);
        
        // Generate monthly progress
        const monthlyData = generateMonthlyProgress(bits);
        setMonthlyProgress(monthlyData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('Failed to load analytics data. Please try again later.');
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [user]);

  // Helper function to calculate streak
  const calculateStreak = (bits: any[]) => {
    if (bits.length === 0) return 0;
    
    // Get all unique dates from both creation and update timestamps
    const activityDates = new Set<string>();
    bits.forEach(bit => {
      // Add creation date
      if (bit.createdAt) {
        const date = new Date(bit.createdAt);
        activityDates.add(date.toDateString());
      }
      // Add update date
      if (bit.updatedAt) {
        const date = new Date(bit.updatedAt);
        activityDates.add(date.toDateString());
      }
    });

    // Convert to array and sort in reverse chronological order
    const sortedDates = Array.from(activityDates)
      .map(dateStr => new Date(dateStr))
      .sort((a, b) => b.getTime() - a.getTime());

    if (sortedDates.length === 0) return 0;

    // Check if most recent activity was today
    const mostRecentDate = sortedDates[0];
    const today = new Date();
    
    if (isSameDay(mostRecentDate, today)) {
      let streak = 1;
      let currentDate = new Date(today);
      currentDate.setDate(currentDate.getDate() - 1);
      
      // Check for consecutive days
      for (let i = 1; i < sortedDates.length; i++) {
        const activityDate = sortedDates[i];
        
        if (isSameDay(activityDate, currentDate)) {
          streak++;
          currentDate.setDate(currentDate.getDate() - 1);
        } else if (activityDate < currentDate) {
          // If we find a date from before the current date, continue checking
          continue;
        } else {
          // If there's a gap, break the streak
          break;
        }
      }
      
      return streak;
    }
    
    return 0;
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Helper function to generate weekly activity data
  const generateWeeklyActivity = (bits: any[]) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const activityByDay = days.map(day => ({
      name: day,
      count: 0
    }));

    const countedDayBitPairs = new Set<string>();

    bits.forEach(bit => {
      // Count both creation and last-edit dates
      [bit.createdAt, bit.updatedAt].forEach(ts => {
        if (!ts) return;
        const date = new Date(ts);
        const dayIndex = date.getDay();
        const key = `${bit.id}-${dayIndex}`;
        if (!countedDayBitPairs.has(key)) {
          countedDayBitPairs.add(key);
          activityByDay[dayIndex].count++;
        }
      });
    });

    return activityByDay;
  };



  // Helper function to generate monthly progress
  const generateMonthlyProgress = (bits: any[]) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Generate all months in calendar order (Jan-Dec) for both current and previous year
    const allMonths = Array.from({ length: 24 }, (_, i) => {
      const monthIndex = i % 12;
      const yearOffset = i < 12 ? 0 : -1;
      const date = new Date(currentYear + yearOffset, monthIndex, 1);
      return {
        date,
        name: date.toLocaleString('en-US', { month: 'short' }),
        sortIndex: i
      };
    });

    // Select only the last 12 months from current month
    const months = allMonths
      .filter(({ date }) => {
        const monthDiff = (currentYear - date.getFullYear()) * 12 + (currentMonth - date.getMonth());
        return monthDiff >= 0 && monthDiff < 12;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const monthlyData = months.map(({ date, name }) => ({
      name,
      bits: 0
    }));

    bits.forEach(bit => {
      const bitDate = new Date(bit.createdAt);
      const matchingMonth = months.find(m => 
        m.date.getMonth() === bitDate.getMonth() && 
        m.date.getFullYear() === bitDate.getFullYear()
      );
      
      if (matchingMonth) {
        const monthIndex = months.indexOf(matchingMonth);
        monthlyData[monthIndex].bits++;
      }
    });

    return monthlyData;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-0 md:p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-0 md:p-4 space-y-8">
      <div className="flex flex-col items-center justify-center text-center mb-8 w-full">
        <h1 className="text-2xl font-bold text-orange-500">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-2">Track your writing progress and activity</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total Bits', value: totalBits },
          { label: 'Total Words', value: totalWords.toLocaleString() },
          { label: 'Avg Words/Bit', value: averageWordsPerBit },
          { label: 'Writing Streak', value: streak === 0 ? '—' : `${streak}d` },
          { label: 'Exercises Done', value: totalExercises },
          { label: 'Exercise Streak', value: exerciseStreak === 0 ? '—' : `${exerciseStreak}d` },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center text-center">
            <span className="text-2xl font-bold text-orange-500">{stat.value}</span>
            <span className="text-xs text-gray-500 mt-1">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Progress to Mastery</h3>
          <div className="h-64 flex flex-col items-center justify-center">
            <div className="relative w-48 h-48">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(#f97316 ${masteryProgress[0]?.value || 0}%, #f1f5f9 0)`,
                  transform: 'rotate(-90deg)',
                }}
              ></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {(masteryProgress[0]?.value || 0) >= 1 ? (
                  <span className="text-3xl font-bold text-orange-500">
                    {(masteryProgress[0]?.value || 0).toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-2xl font-bold text-orange-500">
                    {(masteryProgress[0]?.words || 0).toLocaleString()}
                  </span>
                )}
                <span className="text-sm text-gray-600 text-center px-2">
                  {(masteryProgress[0]?.value || 0) >= 1 ? 'to mastery' : 'words written'}
                </span>
              </div>
            </div>
            <div className="text-center mt-6 space-y-1">
              <p className="text-gray-600 font-medium">
                {(masteryProgress[0]?.words || 0).toLocaleString()} / {WORDS_TO_MASTERY.toLocaleString()} words
              </p>
              <p className="text-sm text-gray-500">Keep writing — every word counts.</p>
            </div>
          </div>
          <div className="text-center mt-2">
            <p className="text-xs text-gray-400">
              Based on the 10,000 hours / 30M words to mastery principle
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Weekly Activity</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyActivity} margin={{ bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Monthly Progress (last 12 months)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyProgress}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="bits" stroke="#f97316" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Activity (Last 12 Weeks)</h3>
        {(() => {
          const weeks = 12;
          const today = new Date();
          const days: { date: string; count: number }[] = [];
          for (let i = weeks * 7 - 1; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(today.getDate() - i);
            const dateStr = new Intl.DateTimeFormat('en-CA').format(d);
            days.push({ date: dateStr, count: activityHeatmap[dateStr] || 0 });
          }
          const maxCount = Math.max(1, ...days.map(d => d.count));
          const cols: { date: string; count: number }[][] = [];
          for (let w = 0; w < weeks; w++) {
            cols.push(days.slice(w * 7, w * 7 + 7));
          }
          const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          return (
            <div className="overflow-x-auto">
              <div className="flex gap-1">
                {/* Day labels column */}
                <div className="flex flex-col gap-1 mr-1">
                  {dayLabels.map((label) => (
                    <div key={label} className="w-7 h-4 flex items-center justify-end">
                      <span className="text-[10px] text-gray-400 leading-none">{label}</span>
                    </div>
                  ))}
                </div>
                {/* Grid columns */}
                {cols.map((col, wi) => (
                  <div key={wi} className="flex flex-col gap-1">
                    {col.map((day) => {
                      const intensity = day.count === 0 ? 0 : Math.ceil((day.count / maxCount) * 4);
                      const bg = ['bg-gray-100', 'bg-orange-100', 'bg-orange-200', 'bg-orange-400', 'bg-orange-600'][intensity];
                      return (
                        <div
                          key={day.date}
                          title={`${day.date}: ${day.count} activit${day.count === 1 ? 'y' : 'ies'}`}
                          className={`w-4 h-4 rounded-sm ${bg} cursor-default`}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1 mt-3">
                <span className="text-xs text-gray-400 mr-1">Less</span>
                {['bg-gray-100','bg-orange-100','bg-orange-200','bg-orange-400','bg-orange-600'].map((bg, i) => (
                  <div key={i} className={`w-4 h-4 rounded-sm ${bg}`} />
                ))}
                <span className="text-xs text-gray-400 ml-1">More</span>
              </div>
            </div>
          );
        })()}
        </div>
      </div>
    </div>
  );
}
